import React, { ReactNode, Ref } from 'react'
import isEmpty from 'lodash/isEmpty'
import orderBy from 'lodash/orderBy'
import { matchSorter } from 'match-sorter'
import CloseIcon from 'transfix-icons/build/CloseIcon'
import DownArrowIcon from 'transfix-icons/build/DownArrowIcon'
import {
  Autocomplete as MuiAutocomplete,
  AutocompleteClassKey as MuiAutocompleteClassKey,
  AutocompleteProps as MuiAutocompleteProps,
  AutocompleteRenderGroupParams as MuiAutocompleteRenderGroupParams,
  AutocompleteRenderInputParams as MuiAutocompleteRenderInputParams,
  AutocompleteRenderOptionState as MuiAutocompleteRenderOptionState,
  AutocompleteFreeSoloValueMapping,
  Box,
  createStyles,
  FilterOptionsState,
  InputAdornment,
  makeStyles,
} from 'transfix-ui/core'
import { brandColors, calcRem, shadows } from '../../theme'
import { Avatar } from '../Avatar'
import { AutocompleteGroup } from './components/AutocompleteGroup'
import { AutocompleteOption } from './components/AutocompleteOption'
import { AutocompleteTextField } from './components/AutocompleteTextField'
import IconForTextField from './components/AutocompleteTextField/IconForTextField'
import { TInputIcon } from './components/AutocompleteTextField/utils'
import {
  TRenderInputType,
  IAutocompleteBaseOption,
  IAutocompleteBaseGroup,
  TFilterOptionType,
} from './types'

// This prop is overrode by a more specific custom preset
type TOmittedProp = 'renderInput'

export type TAutocompleteBaseSharedProps = {
  /**
   * @default false
   */
  disabled?: boolean
  /**
   * @default 'default'
   */
  renderInputType?: TRenderInputType
  /**
   * @example 'User'
   */
  inputIcon?: TInputIcon
  label?: React.ReactNode | string
  /**
   * @default false
   */
  required?: boolean
  inputError?: boolean
  inputValidationMessage?: string
  /**
   * Enables fuzzy search/filtering
   * @default 'default'
   */
  filterOptionsType?: TFilterOptionType
  /**
   * @example 'Jane Marie'
   */
  placeholder?: string
  /**
   * The size of the autocomplete.
   * @default 'medium'
   */
  size?: 'small' | 'medium'
}

export interface IAutocompleteBaseProps<
  T extends IAutocompleteBaseOption,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
>
  extends Omit<MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>, TOmittedProp>,
    TAutocompleteBaseSharedProps {}

/**
 * Default label function for <Autocomplete />
 */
function getOptionLabel<T extends IAutocompleteBaseOption, FreeSolo extends boolean | undefined>(
  option: T | AutocompleteFreeSoloValueMapping<FreeSolo>
): string {
  const { text = '' } = (option || {}) as T
  return text
}

/**
 * Groups/sorts options by `groupIndex` prop on IAutocompleteOption
 */
function groupBy<T extends IAutocompleteBaseOption>(option: T): string {
  const { groupIndex } = option || {}
  return `${groupIndex}`
}

/**
 * Used for `renderOption` prop of <Autocomplete />
 */
function renderOption<T extends IAutocompleteBaseOption>(
  props: React.HTMLAttributes<HTMLLIElement>,
  option: T,
  state: MuiAutocompleteRenderOptionState
): ReactNode {
  return <AutocompleteOption option={option} state={state} key={option.id} {...props} />
}

/**
 * Used for `renderGroup` prop of <Autocomplete />
 */
function renderGroup(
  params: MuiAutocompleteRenderGroupParams,
  optionGroupsByGroupIndex: Record<number, IAutocompleteBaseGroup>
): React.ReactNode {
  return (
    <AutocompleteGroup
      params={params}
      key={params.key}
      optionGroupsByGroupIndex={optionGroupsByGroupIndex}
    />
  )
}

/**
 * High-order function that adds extra params to `renderGroup` function prop
 */
function getRenderGroup(optionGroupsByGroupIndex: Record<number, IAutocompleteBaseGroup>) {
  return (params: MuiAutocompleteRenderGroupParams) => renderGroup(params, optionGroupsByGroupIndex)
}

interface IGetRenderInputOptions {
  label?: React.ReactNode | string
  placeholder?: string
  inputIcon?: TInputIcon
  helperText?: string
  error?: boolean
  required?: boolean
  multiple?: boolean
}

/**
 * Used for <Autocomplete />'s `renderInput` prop
 */
const renderInput = (
  params: MuiAutocompleteRenderInputParams,
  renderInputType: TRenderInputType = 'default',
  props?: IGetRenderInputOptions
) => {
  const { inputProps } = (params || {}) as any
  const { value: name } = inputProps || {}
  const { error, helperText, inputIcon, label, multiple, placeholder, required } = props || {}

  if (multiple) {
    return (
      <AutocompleteTextField
        {...params}
        placeholder={placeholder}
        helperText={helperText}
        error={error}
        required={required}
      />
    )
  }

  switch (renderInputType) {
    case 'withIcon':
      return (
        <AutocompleteTextField
          {...params}
          {...props}
          required={required}
          label={label}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position='start'>
                <Box component='div' display='inline-block' pr='3px'>
                  <IconForTextField iconName={inputIcon} />
                </Box>
              </InputAdornment>
            ),
          }}
        />
      )
    case 'withAvatar':
      return (
        <AutocompleteTextField
          {...params}
          {...props}
          label={label}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position='start'>
                <Box component='div' display='inline-block' pr='3px'>
                  <Avatar name={name || 'Unassigned'} size='xsmall' hideTooltip />
                </Box>
              </InputAdornment>
            ),
          }}
        />
      )
    default:
      return <AutocompleteTextField {...params} {...props} />
  }
}

/**
 * High-order function that adds extra params to `renderInput` for <Autocomplete />
 */
function getRenderInput(renderInputType: TRenderInputType, options: IGetRenderInputOptions) {
  return (params: MuiAutocompleteRenderInputParams) => {
    return renderInput(params, renderInputType, options)
  }
}

/**
 * Orders options by `groupIndex`
 */
function getOptions<T extends IAutocompleteBaseOption>(options: readonly T[]): readonly T[] {
  return orderBy(options, 'groupIndex')
}

/**
 * Used for <Autocomplete />'s `getOptionSelected` prop
 */
function isOptionEqualToValue<T extends IAutocompleteBaseOption>(option: T, value: T): boolean {
  const { id: optionId } = option || {}
  const { id: valueId } = value || {}
  return optionId === valueId
}

/**
 * Returns an object, keyed by `groupIndex`, based on `options` values
 * @example
 * {
 *   1: { groupName: 'Customers', groupIndex: 1 },
 *   2: { groupName: 'Dispatchers', groupIndex: 2 }
 * }
 */
export function getOptionGroupDetails<T extends IAutocompleteBaseOption>(
  options: readonly T[]
): Record<number, IAutocompleteBaseGroup> {
  const groupsByGroupIndex: Record<number, IAutocompleteBaseGroup> = {}

  for (const option of options) {
    const { dividerAfter, dividerBefore, groupIndex, groupName } = option || {}

    if (isEmpty(groupsByGroupIndex[groupIndex])) {
      groupsByGroupIndex[groupIndex] = {
        dividerAfter,
        dividerBefore,
        groupIndex,
        groupName,
      }
    }
  }

  return groupsByGroupIndex
}

/**
 * Enables fuzzy search/filtering which matches
 * any substring withing the option label
 * @see https://material-ui.com/components/autocomplete/#
 */
function getFilterOptionsProp(filterOptionType: TFilterOptionType) {
  switch (filterOptionType) {
    /**
     * @note Sorts items in array first by index,
     * but matches text search based on `text` key.
     *
     * Used for <AutocompleteTime />, or other time-related components
     */
    case 'time':
      return function fuzzyTimeSearch<T>(
        options: T[] = [],
        { inputValue = '' }: FilterOptionsState<T>
      ) {
        return matchSorter(options, inputValue, {
          keys: ['text'],
          baseSort: (a, b) => (a.index < b.index ? -1 : 1),
        })
      }
    case 'fuzzy':
      return function fuzzySearch<T>(
        options: T[] = [],
        { inputValue = '' }: FilterOptionsState<T>
      ) {
        return matchSorter(options, inputValue, {
          /**
           * Uses the `text` property on the option to match string input
           */
          keys: ['text'],
          /**
           * Default sorting ranks options by array item index.
           * @note For location-based results, this is useful since API array order is intentional.
           */
          baseSort: (a, b) => (a.index < b.index ? -1 : 1),
        })
      }
    case 'default':
    default:
      return undefined
  }
}

/**
 * Only allow specific classes to be stylable
 * @see https://v4.mui.com/api/autocomplete/#css
 */
type TAutocompleteClassKey = Extract<
  MuiAutocompleteClassKey,
  | 'listbox'
  | 'paper'
  | 'root'
  | 'option'
  | 'noOptions'
  | 'inputRoot'
  | 'popupIndicator'
  | 'clearIndicator'
  | 'endAdornment'
>

const useAutocompleteStyles = makeStyles(theme =>
  createStyles<TAutocompleteClassKey, { inputError?: boolean }>({
    root: {
      // Removes label's top border
      '& [class*="legendNotched"] span': {
        display: 'none',
      },
      // Sets border and error message color when there is an input error
      '& .MuiFormHelperText-root': props =>
        props.inputError ? { color: brandColors.error } : undefined,
      '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': props =>
        props.inputError
          ? {
              borderColor: brandColors.error,
              '&:hover': {
                borderColor: brandColors.error,
              },
            }
          : undefined,
    },
    /**
     * Uses some custom spacing that doesn't use base spacing value (8px)
     */
    inputRoot: {
      backgroundColor: brandColors.white,
      paddingTop: '2.19px !important',
      paddingBottom: '2.19px !important',
      paddingLeft: '6px !important',

      '&.MuiOutlinedInput-adornedStart': {
        paddingLeft: '9px !important',
      },

      // Small size
      '&.MuiOutlinedInput-sizeSmall': {
        paddingTop: '3.185px !important',
        paddingBottom: '3.185px !important',

        '&.MuiOutlinedInput-adornedStart': {
          paddingLeft: '6px !important',
        },
      },
    },
    listbox: {
      padding: 0,
    },
    paper: {
      marginTop: 0,
      borderRadius: `0 0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px`, // 0 0 4px 4px
      marginLeft: 0,
      boxShadow: shadows[5],
    },
    noOptions: {
      fontSize: calcRem(14),
      padding: `${theme.spacing(1)} calc(${theme.spacing(1)} + 2px)`, // 8px 10px
    },
    option: {
      padding: `${theme.spacing(1)} calc(${theme.spacing(1)} + 2px)`, // 8px 10px
      /**
       * Selected styles
       */
      '&[aria-selected="true"]': {
        backgroundColor: brandColors.skyBlue2,
        borderColor: brandColors.skyBlue2,
        color: brandColors.coolGray8,
      },
      /**
       * Hover styles
       */
      '&.Mui-focused': {},
    },
    clearIndicator: {
      color: brandColors.skyBlue6,

      '&:hover': {
        color: brandColors.skyBlue6,
      },

      '.Mui-error &': {
        color: brandColors.error1,

        '&:hover': {
          color: brandColors.error1,
        },
      },
    },
    popupIndicator: {
      padding: 4,
      marginLeft: 2,
      color: brandColors.skyBlue6,

      '&:hover': {
        color: brandColors.skyBlue6,
      },
    },
    /**
     * For small inputs, override the vertical placement of the endAdornment
     */
    endAdornment: {
      '.MuiOutlinedInput-sizeSmall &': {
        top: 'calc(50% - 12px)',
      },

      /**
       * Overrides legacy styles for disabled buttons
       * @see src/assets/scss/2_tools/_on-event.scss
       */
      '& .MuiIconButton-root.Mui-disabled': {
        backgroundColor: 'transparent !important',
        borderColor: 'transparent !important',
      },
    },
  })
)

const Autocomplete = React.forwardRef(function Autocomplete<
  T extends IAutocompleteBaseOption,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined
>(
  {
    classes: classesProp = {},
    disabled = false,
    size: sizeProp = 'medium',
    groupBy: groupByProp = groupBy,
    getOptionLabel: getOptionLabelProp = getOptionLabel,
    options: optionsProp = [],
    clearOnBlur = true,
    clearOnEscape = true,
    openOnFocus = true,
    includeInputInList = false,
    selectOnFocus = true,
    autoHighlight = true,
    autoSelect = true,
    autoComplete = true,
    renderInputType = 'default',
    label = '',
    multiple,
    placeholder = '',
    value: valueProp,
    inputIcon,
    inputError,
    inputValidationMessage,
    filterOptionsType = 'default',
    required,
    ...props
  }: IAutocompleteBaseProps<T, Multiple, DisableClearable, FreeSolo>,
  ref?: Ref<HTMLInputElement>
) {
  const filterOptions = getFilterOptionsProp(filterOptionsType)
  const optionGroupsByGroupIndex = getOptionGroupDetails(optionsProp)
  const renderGroup = getRenderGroup(optionGroupsByGroupIndex)
  const renderInput = getRenderInput(renderInputType, {
    label,
    placeholder,
    inputIcon,
    error: inputError,
    helperText: inputValidationMessage,
    required,
    multiple,
  })
  const options = getOptions(optionsProp)
  const classes = { ...useAutocompleteStyles({ inputError }), ...classesProp }

  const getOptionDisabled = (option: IAutocompleteBaseOption) => {
    const { disabled = false } = option || {}
    return disabled
  }

  return (
    <MuiAutocomplete
      value={valueProp}
      classes={classes}
      disabled={disabled}
      ref={ref}
      multiple={multiple}
      renderInput={renderInput}
      getOptionLabel={getOptionLabelProp}
      size={sizeProp}
      groupBy={groupByProp}
      options={options}
      renderOption={renderOption}
      renderGroup={renderGroup}
      isOptionEqualToValue={isOptionEqualToValue}
      clearOnBlur={clearOnBlur}
      clearOnEscape={clearOnEscape}
      openOnFocus={openOnFocus}
      includeInputInList={includeInputInList}
      selectOnFocus={selectOnFocus}
      placeholder={placeholder}
      autoHighlight={autoHighlight}
      autoSelect={autoSelect}
      autoComplete={autoComplete}
      popupIcon={<DownArrowIcon size={sizeProp === 'medium' ? 'large' : 'medium'} />}
      clearIcon={<CloseIcon size={sizeProp === 'medium' ? 'large' : 'medium'} />}
      filterOptions={filterOptions}
      getOptionDisabled={getOptionDisabled}
      {...props}
    />
  )
})

export default Autocomplete
